import React, { useState } from 'react';
import I18n from '@/modules/i18n';
import Modal from '@/components/common/Modal';
import { AI_GRADE_BANDS, GRADE_BAND_LABELS, type GradeBand } from '../constants';
import CompareColumn from './CompareColumn';
import CompareFooter from './CompareFooter';
import CompareHeader from './CompareHeader';
import useCompareTexts from './useCompareTexts';

interface CompareReadingLevelsModalProps {
  availableGradeBands: string[];
  classroomId?: number;
  fontSize?: string;
  initialGradeBand: GradeBand;
  isOpen: boolean;
  locale?: string;
  programGradeBands: string[];
  programId?: number;
  sectionEndPage?: number;
  sectionId: number;
  sectionStartPage?: number;
  sectionTitle?: string;
  studentCountsByLevel: Record<string, number>;
  onClose: () => void;
}

const CompareReadingLevelsModal = ({
  availableGradeBands,
  classroomId,
  fontSize,
  initialGradeBand,
  isOpen,
  locale = 'en',
  programGradeBands,
  programId,
  sectionEndPage,
  sectionId,
  sectionStartPage,
  sectionTitle,
  studentCountsByLevel,
  onClose,
}: CompareReadingLevelsModalProps) => {
  const [selectedGradeBand, setSelectedGradeBand] = useState<GradeBand>(initialGradeBand);
  const { aiText, error, isLoading, standardText } = useCompareTexts(sectionId, selectedGradeBand, locale);

  const availableAIBands = AI_GRADE_BANDS
    .filter(band => availableGradeBands.includes(band) && programGradeBands.includes(band));

  const headerContent = (
    <CompareHeader
      availableAIBands={availableAIBands}
      classroomId={classroomId}
      programId={programId}
      sectionEndPage={sectionEndPage}
      sectionStartPage={sectionStartPage}
      sectionTitle={sectionTitle}
      selectedGradeBand={selectedGradeBand}
      studentCount={studentCountsByLevel[selectedGradeBand] || 0}
      onClose={onClose}
      onGradeBandChange={setSelectedGradeBand}
    />
  );

  return (
    <Modal
      nearlyFullScreen
      shouldCloseOnOverlayClick
      bodyClassName="!tw-p-0 !tw-flex !tw-flex-1 !tw-min-h-0"
      closeable={false}
      closeModal={onClose}
      footer={<CompareFooter onClose={onClose} />}
      headerClass="!tw-p-0 !tw-h-auto"
      headerText={headerContent}
      headerTextClassName="tw-w-full tw-m-0 tw-text-[inherit] tw-font-[inherit]"
      isOpen={isOpen}
    >
      {error ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-flex-1 tw-text-red">
          {error}
        </div>
      ) : (
        <div className="tw-flex tw-flex-1 tw-min-h-0 tw-w-full">
          <CompareColumn
            bodyHtml={standardText?.body_html}
            fontSize={fontSize}
            isLoading={isLoading && !standardText}
            label={I18n.t('standard_level')}
            labelClassName="tw-text-tci-black"
          />
          <div className="tw-w-px tw-bg-med-grey tw-shrink-0" />
          <CompareColumn
            showAIBadge
            bodyHtml={aiText?.body_html}
            fontSize={fontSize}
            gradeBand={selectedGradeBand}
            isLoading={isLoading && !aiText}
            label={GRADE_BAND_LABELS[selectedGradeBand]}
            labelClassName="tw-text-tci-black"
            llmCompletionId={aiText?.llm_completion_id}
            locale={locale}
            sectionId={sectionId}
          />
        </div>
      )}
    </Modal>
  );
};

export default CompareReadingLevelsModal;
